/**
 * perfReporter — perf 진단 로그 출력 정책 순수 코어.
 *
 * 렉이 심한 순간에 console.warn/error를 파일 캡처와 연결하면 계측 로그 자체가 native I/O를
 * 늘릴 수 있다. reporter는 출력 레벨, 최소 간격, 최대 라인 수를 한 곳에서 제어해 앱별
 * 로그 sink(logcat, 파일, 원격 수집)를 안전하게 교체하게 한다.
 */

export type PerfReportLevel = "debug" | "info" | "warn" | "error";

export interface PerfConsoleReporterOptions {
  /** 출력 레벨. native 파일 캡처를 피하려면 info/debug를 쓴다. 기본 info. */
  readonly level?: PerfReportLevel;
  /** 같은 reporter에서 로그를 낼 최소 간격 ms. 기본 0. */
  readonly minIntervalMs?: number;
  /** 세션당 최대 출력 라인 수. 기본 무제한. */
  readonly maxLines?: number;
  /** 시간 소스. 테스트 주입용. */
  readonly now?: () => number;
  /** 실제 출력 sink. 기본 console[level]. */
  readonly sink?: (level: PerfReportLevel, line: string) => void;
}

export interface PerfConsoleReporter {
  /** 정책을 통과하면 출력하고 true, 억제되면 false. */
  report(line: string): boolean;
  /** 지금까지 실제 출력한 라인 수. */
  emitted(): number;
}

const defaultNow = (): number =>
  typeof performance !== "undefined" && typeof performance.now === "function"
    ? performance.now()
    : Date.now();

const defaultSink = (level: PerfReportLevel, line: string): void => {
  const fn = console[level] ?? console.log;
  fn.call(console, line);
};

export function createPerfConsoleReporter(
  opts: PerfConsoleReporterOptions = {},
): PerfConsoleReporter {
  const level = opts.level ?? "info";
  const minIntervalMs = opts.minIntervalMs ?? 0;
  const maxLines = opts.maxLines ?? Number.POSITIVE_INFINITY;
  const now = opts.now ?? defaultNow;
  const sink = opts.sink ?? defaultSink;
  let lastEmitAt = Number.NEGATIVE_INFINITY;
  let emittedCount = 0;

  return {
    report(line: string): boolean {
      if (emittedCount >= maxLines) return false;
      const t = now();
      if (t - lastEmitAt < minIntervalMs) return false;
      lastEmitAt = t;
      emittedCount += 1;
      sink(level, line);
      return true;
    },
    emitted: () => emittedCount,
  };
}
